import { useEffect } from 'react';
import { LocalizedText, Form } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { FloatingPanel } from '@mews/b2b-ui';
import { isDefaultAgeCategoryForService } from '@mews-commander/legacy-entities/src/ducks/ageCategoriesNew';
import { useClaimOrderItemsMutation } from '../../../mutations/claimOrderItemsMutation';
import { PanelContentWrapper } from '../PanelContentWrapper';
import { ProductCheckListDataRow } from '../../../types';
import { useFilteredProductsData } from '../../cells/utils/useFilteredProductsData';
import { useAgeCategoriesContext } from '../../../contexts/AgeCategoriesContext';
import { getOrderItemClaims } from './utils';
import { ClaimPerPersonFormFields } from './ClaimPerPersonFormFields';

export interface ClaimPerPersonValues {
    date: string;
}

interface ClaimPerPersonPanelProps {
    opened: boolean;
    onClose: () => void;
    row: ProductCheckListDataRow;
}

export const ClaimPerPersonPanel: React.FC<ClaimPerPersonPanelProps> = ({
    opened,
    row,
    row: { owner, resourceId, productsData, serviceId, productServiceOrder },
    onClose,
}) => {
    const { sortedAgeCategories } = useAgeCategoriesContext();
    const claimOrderItemsMutation = useClaimOrderItemsMutation();
    const filteredProductsData = useFilteredProductsData(productsData);

    const defaultAgeCategory = sortedAgeCategories.find(isDefaultAgeCategoryForService(serviceId));

    useEffect(() => {
        if (!claimOrderItemsMutation.isPending) {
            onClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [claimOrderItemsMutation.isPending]);

    const onSubmit = (values: Record<string, number>) => {
        const orderItemClaims = getOrderItemClaims(
            values,
            filteredProductsData,
            !!productServiceOrder,
            defaultAgeCategory?.id
        );
        claimOrderItemsMutation.mutate({ orderItemClaims });
    };

    return (
        <Form onSubmit={onSubmit}>
            {({ submit }) => (
                <FloatingPanel
                    active={opened}
                    onClose={onClose}
                    title={<LocalizedText textKey={TextKey.ClaimPerPerson} />}
                    size="small"
                    loading={claimOrderItemsMutation.isPending}
                    actions={[
                        {
                            variant: 'primary',
                            children: <LocalizedText textKey={TextKey.Save} />,
                            onClick: submit,
                            type: 'submit',
                        },
                    ]}
                >
                    <PanelContentWrapper owner={owner} resourceId={resourceId}>
                        <ClaimPerPersonFormFields {...row} productsData={filteredProductsData} />
                    </PanelContentWrapper>
                </FloatingPanel>
            )}
        </Form>
    );
};
